<button
    #triggerBtn
    type="button"
    class="tenant-account-logos cipo--btn-reset"
    data-testid="account-menu-trigger"
    (click)="toggleMenu()"
    [attr.aria-label]="'a11y.header.accountMenu' | translate"
    [attr.aria-expanded]="isOpen()"
    aria-haspopup="dialog">
    <cipo-img-base64
        [base64]="tenantStore.selectedTenant()?.logo"
        [height]="40"
        alt=""
        fxShow
        fxHide.lt-md
        data-testid="account-menu-tenant-logo"
        placeholder="/src/assets/images/CIPO NoLogo.png"></cipo-img-base64>
    @if (userStore.userData()?.logo) {
        <cipo-img-base64
            [base64]="userStore.userData()?.logo"
            [height]="40"
            alt=""
            radius="50%"
            data-testid="account-menu-user-logo"></cipo-img-base64>
    } @else {
        <mat-icon class="user-nologo" aria-hidden="true" data-testid="account-menu-user-no-logo">
            account_circle
        </mat-icon>
    }
</button>

<!-- Hidden MatMenu — never opens visually; provides a real MatMenu instance injected
     into the overlay portal so MatMenuTrigger._parentMaterialMenu passes `instanceof MatMenu`,
     enabling the ◄ arrow class and lateral submenu positioning on the Demo button. -->
<mat-menu #panelCtx></mat-menu>

<ng-template #overlayTpl>
    <div
        cdkTrapFocus
        [cdkTrapFocusAutoCapture]="true"
        class="mat-mdc-menu-panel mat-elevation-z4 reversed-menu"
        role="dialog"
        [attr.aria-label]="'a11y.header.accountMenu' | translate"
        (keydown.escape)="closeMenu()">
        <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
        <div (click)="$event.stopPropagation()" class="user-container" data-testid="account-menu-user-container">
            <cipo-user-profile-header
                fxFlex="none"
                [profileImageSrc]="userStore.userData()?.logo"
                [firstName]="userStore.userData()?.firstName"
                [lastName]="userStore.userData()?.lastName"
                [email]="userStore.userData()?.email"
                [organizationName]="userStore.userData()?.organizationName"
                displayFonts="small"
                [enableProfileUpload]="true"
                [roles]="userStore.rolesName()"
                [maxRoles]="1"></cipo-user-profile-header>
        </div>

        <!-- Actions: role="menu" contains only role="menuitem" children — valid ARIA -->
        <div class="mat-mdc-menu-content" role="menu">
            <button
                mat-menu-item
                (click)="goToProfile()"
                (mouseenter)="closeDemo()"
                data-testid="account-menu-user-profile">
                <mat-icon aria-hidden="true">account_circle</mat-icon>
                <span>{{ "header.myProfile" | translate }}</span>
            </button>
            @if (tenantStore.availableTenants().length !== 1) {
                <button
                    mat-menu-item
                    (click)="goToTenants()"
                    (mouseenter)="closeDemo()"
                    data-testid="account-menu-switch-tenant">
                    <mat-icon class="material-symbols-outlined" aria-hidden="true">tenancy</mat-icon>
                    <span>{{ "header.switchTenant" | translate }}</span>
                </button>
            }
            <button
                mat-menu-item
                (click)="openAccessibility()"
                (mouseenter)="closeDemo()"
                data-testid="account-menu-accessibility">
                <mat-icon class="material-symbols-outlined" aria-hidden="true">accessibility_new</mat-icon>
                <span>{{ "header.accessibility" | translate }}</span>
            </button>
            <button mat-menu-item [matMenuTriggerFor]="themeMode" data-testid="demo-theme-mode">
                <mat-icon aria-hidden="true">contrast</mat-icon>
                <span>{{ "header.theme.mode" | translate }}</span>
            </button>
            <button mat-menu-item (click)="signOut()" (mouseenter)="closeDemo()" data-testid="account-menu-sign-out">
                <mat-icon aria-hidden="true">logout</mat-icon>
                <span>{{ "header.signOut" | translate }}</span>
            </button>
            @if (userStore.devMode()) {
                <button
                    #demoBtn
                    mat-menu-item
                    [matMenuTriggerFor]="demo"
                    #demoTrigger="matMenuTrigger"
                    (mouseenter)="demoTrigger.openMenu()"
                    (menuClosed)="demoBtn.focus()"
                    data-testid="account-menu-demo">
                    <mat-icon aria-hidden="true">science</mat-icon>
                    <span>Demo</span>
                </button>
            }
        </div>
    </div>
</ng-template>

<mat-menu #demo="matMenu" xPosition="before">
    <button mat-menu-item [matMenuTriggerFor]="theme" data-testid="demo-theme-color">
        <mat-icon aria-hidden="true">palette</mat-icon>
        <span>{{ "header.theme.color" | translate }}</span>
    </button>
    <button mat-menu-item [matMenuTriggerFor]="language" data-testid="demo-language">
        <mat-icon aria-hidden="true">language</mat-icon>
        <span>{{ "header.language.title" | translate }}</span>
    </button>
    <button mat-menu-item (click)="openPalette()" data-testid="demo-open-palette">
        <mat-icon aria-hidden="true">invert_colors</mat-icon>
        <span>{{ "header.openPalette" | translate }}</span>
    </button>
</mat-menu>

<mat-menu #theme="matMenu">
    <button mat-menu-item (click)="changeTheme('default')" data-testid="theme-color-default">
        <mat-icon class="default-color" aria-hidden="true">opacity</mat-icon>
        <span>{{ "header.theme.default" | translate }}</span>
    </button>
    <button mat-menu-item (click)="changeTheme('orange')" data-testid="theme-color-orange">
        <mat-icon class="orange-color" aria-hidden="true">opacity</mat-icon>
        <span>{{ "header.theme.orange" | translate }}</span>
    </button>
</mat-menu>

<mat-menu #themeMode="matMenu">
    <button mat-menu-item (click)="changeThemeMode('light')" data-testid="theme-mode-light">
        <mat-icon class="cipo-text--accent-500" aria-hidden="true">light_mode</mat-icon>
        <span>{{ "header.theme.light" | translate }}</span>
    </button>
    <button mat-menu-item (click)="changeThemeMode('dark')" data-testid="theme-mode-dark">
        <mat-icon aria-hidden="true">dark_mode</mat-icon>
        <span>{{ "header.theme.dark" | translate }}</span>
    </button>
    <button mat-menu-item (click)="changeThemeMode('browser')" data-testid="theme-mode-browser">
        <mat-icon aria-hidden="true">web</mat-icon>
        <span>{{ "header.theme.browser" | translate }}</span>
    </button>
</mat-menu>

<mat-menu #language="matMenu">
    <button mat-menu-item (click)="changeLanguage(languageType.English)" data-testid="language-english">
        <span>{{ "header.language.english" | translate }}</span>
    </button>
    <button mat-menu-item (click)="changeLanguage(languageType.Romanian)" data-testid="language-romanian">
        <span>{{ "header.language.romanian" | translate }}</span>
    </button>
</mat-menu>
